import { z } from 'zod';

export const SERVICE_LOG_LEVELS = ['trace', 'debug', 'info', 'warn', 'error', 'fatal'] as const;
export const ServiceLogLevelSchema = z.enum(SERVICE_LOG_LEVELS);

/** Cursors identify a position in the retained API log files, not a timestamp. */
export const ServiceLogQuerySchema = z
  .object({
    source: z.literal('api').default('api'),
    since: z.iso.datetime({ offset: true }).optional(),
    until: z.iso.datetime({ offset: true }).optional(),
    level: ServiceLogLevelSchema.optional(),
    query: z.string().max(500).optional(),
    before: z
      .string()
      .regex(/^[A-Za-z0-9_-]{1,100}$/)
      .optional(),
    after: z
      .string()
      .regex(/^[A-Za-z0-9_-]{1,100}$/)
      .optional(),
    limit: z.coerce.number().int().min(1).max(500).default(200),
  })
  .superRefine((query, context) => {
    if (query.before && query.after) {
      context.addIssue({ code: 'custom', message: 'Use either before or after, not both.' });
    }
    if (query.since && query.until && Date.parse(query.since) > Date.parse(query.until)) {
      context.addIssue({ code: 'custom', message: 'The start time must precede the end time.' });
    }
  });

export const ServiceLogEntrySchema = z.object({
  id: z.string(),
  at: z.iso.datetime(),
  level: ServiceLogLevelSchema,
  source: z.literal('api'),
  /** Exact JSON text emitted by the sanitized logger, without its final newline. */
  raw: z.string(),
  message: z.string().optional(),
  requestId: z.string().optional(),
});

export const AdminServiceLogsSchema = z.object({
  entries: z.array(ServiceLogEntrySchema),
  before: z.string().nullable(),
  /** High-water position: advances even when the filters matched no new entries. */
  after: z.string().nullable(),
  /** Further records are available after this page; fetch them before waiting to poll. */
  hasMore: z.boolean(),
  cursorExpired: z.boolean(),
  source: z.object({
    id: z.literal('api'),
    label: z.literal('API'),
    available: z.boolean(),
    oldestAt: z.iso.datetime().nullable(),
    retentionBytes: z.number().int().nonnegative(),
    notice: z.string(),
  }),
});

export type ServiceLogLevel = z.infer<typeof ServiceLogLevelSchema>;
export type ServiceLogQuery = Omit<z.input<typeof ServiceLogQuerySchema>, 'limit'> & {
  limit?: number;
};
export type ServiceLogEntry = z.infer<typeof ServiceLogEntrySchema>;
export type AdminServiceLogs = z.infer<typeof AdminServiceLogsSchema>;
